import React from "react";
import { HABITAT_COLORS, type ViewMode } from "../../vowGameLogic";
import { SCORE_METADATA } from "../scoreMetadata";
import InfoPopout from "components/Wrappers/InfoPopout";
import { VowLegendTooltips } from "../../utils/VowToolTipConst";
import { ACTION_ICONS, TILE_STATUS_ICONS } from "../iconAssets";
import { PANEL_CLASS } from "../uiConstants";

type LegendPanelProps = {
  viewMode: ViewMode;
};

const LegendPanel: React.FC<LegendPanelProps> = ({ viewMode }) => (
  <div className="flex h-full w-full flex-col gap-3 lg:flex-shrink-0">
    <LegendCard title="Legend" fullHeight>
      <ResourceLegend view={viewMode} />
    </LegendCard>
  </div>
);

export const LegendActionsPanel: React.FC = () => (
  <LegendCard title="Actions">
    <ActionLegend />
  </LegendCard>
);

const ResourceLegend: React.FC<{ view: ViewMode }> = ({ view }) => {
  if (view === "habitat") return getHabitatsLegend();
  if (view === "water_level") return getWaterLegend();

  const metricLabel =
    SCORE_METADATA[view as keyof typeof SCORE_METADATA]?.label ?? "Indicator";
  return getGradientLegend(metricLabel);
};

const getHabitatsLegend = () => (
  <div className="flex flex-col gap-3 text-left text-xs text-slate-800">
    <LegendDot color={HABITAT_COLORS.B} label="Woodland">
      <InfoPopout maxWidth={400} popupContent={VowLegendTooltips['Woodland'] ?? (<div>Woodland</div>)} />
    </LegendDot>
    <LegendDot color={HABITAT_COLORS.R} label="Reedbed">
      <InfoPopout maxWidth={400} popupContent={VowLegendTooltips['Reedbed'] ?? (<div>Reedbed</div>)} />
    </LegendDot>
    <LegendDot color={HABITAT_COLORS.P} label="Meadow" withBorder>
      <InfoPopout maxWidth={400} popupContent={VowLegendTooltips['Meadow'] ?? (<div>Meadow</div>)} />
    </LegendDot>
    <LegendDot color={HABITAT_COLORS.E} label="Open Water">
      <InfoPopout maxWidth={400} popupContent={VowLegendTooltips['Open Water'] ?? (<div>Open Water</div>)} />
    </LegendDot>
  </div>
);

const getWaterLegend = () => (
  <div className="flex flex-col gap-2 text-left text-xs text-slate-800">
    <LegendDot color="#3b82f6" label="Flooded (>20)" />
    <LegendDot color="#93c5fd" label="Wet (0-20)" />
    <LegendDot color="#a87b53" label="Dry (-20-0)" />
    <LegendDot color="#6b4e33" label="Very Dry (<-20)" />
  </div>
);

const getGradientLegend = (title: string) => (
  <div className="flex flex-col items-center gap-2 text-xs text-slate-800">
    <span className="text-xs font-semibold text-slate-900">{title}</span>
    <span className="text-xs text-slate-600">High</span>
    <div
      className="h-24 w-4 rounded"
      style={{
        background:
          "linear-gradient(to top, rgba(79, 70, 229, 0.1), rgba(79, 70, 229, 1))",
      }}
    />
    <span className="text-xs text-slate-600">Low</span>
  </div>
);

const ACTION_LEGEND_ITEMS = [
  { key: "graze", icon: ACTION_ICONS.graze, label: "Graze Livestock" },
  { key: "harvest", icon: ACTION_ICONS.harvest, label: "Harvest Reeds" },
  { key: "hunt", icon: ACTION_ICONS.hunt, label: "Hunt" },
  { key: "protect", icon: ACTION_ICONS.protect, label: "Establish Nature Reserve" },
  { key: "install_nest", icon: ACTION_ICONS.install_nest, label: "Install Nest Platform" },
] as const;

const ACTION_STATUS_ITEMS = [
  { key: "protected", icon: TILE_STATUS_ICONS.protected, label: "Nature Reserve", tooltipKey: "Nature Reserve" },
  { key: "nest", icon: TILE_STATUS_ICONS.nest, label: "Nesting Location", tooltipKey: "Nest" },
  { key: "nestSuccess", icon: TILE_STATUS_ICONS.nestSuccess, label: "Nesting Success", tooltipKey: "Success" },
] as const;

const ActionLegend: React.FC = () => (
  <div className="space-y-3 text-xs text-slate-800">
    <div>
      <p className="font-semibold text-slate-900">Player Actions</p>
      <div className="mt-2 flex flex-wrap gap-3">
        {ACTION_LEGEND_ITEMS.map(({ key, icon, label }) => (
          <LegendStatusIcon key={key} icon={icon} label={label} />
        ))}
      </div>
    </div>
    <div>
      <p className="font-semibold text-slate-900">Tile Markers</p>
      <div className="mt-2 flex flex-wrap gap-3">
        {ACTION_STATUS_ITEMS.map(({ key, icon, label, tooltipKey }) => (
          <LegendStatusIcon
            key={key}
            icon={icon}
            label={label}
            tooltipKey={tooltipKey}
          />
        ))}
      </div>
    </div>
  </div>
);

const LegendCard: React.FC<{ title: string; children: React.ReactNode; fullHeight?: boolean }> = ({
  title,
  children,
  fullHeight = false,
}) => (
  <div className={`${PANEL_CLASS} ${fullHeight ? "h-full" : ""} flex flex-col p-3`}>
    <h4 className="text-center text-sm font-semibold text-slate-900">
      {title}
    </h4>
    <div className={`mt-2 ${fullHeight ? "flex-1" : ""}`}>{children}</div>
  </div>
);

const LegendDot: React.FC<{ color: string; label: string; withBorder?: boolean; children?: React.ReactNode }> = ({
  color,
  label,
  withBorder = false,
  children,
}) => (
  <div className="flex items-center gap-2">
    <span
      className={`inline-block h-3 w-3 ${withBorder ? "border border-white/40" : ""}`}
      style={{ backgroundColor: color }}
    />
    <span>{label}</span>
    {children}
  </div>
);

type LegendStatusIconProps = {
  icon: { src: string; alt: string };
  label: string;
  tooltipKey?: string;
  children?: React.ReactNode;
};

const LegendStatusIcon: React.FC<LegendStatusIconProps> = ({
  icon,
  label,
  tooltipKey,
  children,
}) => (
  <div className="flex items-center gap-2">
    <img src={icon.src} alt={icon.alt} className="h-4 w-4 object-contain" />
    <span>{label}</span>
    {children ??
      (tooltipKey ? (
        <InfoPopout
          maxWidth={400}
          popupContent={VowLegendTooltips[tooltipKey] ?? <div />}
        />
      ) : null)}
  </div>
);

export default LegendPanel;
